import * as React from 'react';
import { MultiLevelMenu } from '@react-admin/ra-navigation';
import { usePermissions } from 'react-admin';
import WorkIcon from '@mui/icons-material/Work';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import SourceIcon from '@mui/icons-material/Source';
import LabelIcon from '@mui/icons-material/Label';
import BlockIcon from '@mui/icons-material/Block';
import SecurityIcon from '@mui/icons-material/Security';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import ContactsIcon from '@mui/icons-material/Contacts';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ForumIcon from '@mui/icons-material/Forum';
import QuickreplyIcon from '@mui/icons-material/Quickreply';
import QueueIcon from '@mui/icons-material/Queue';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { resourcePermissionMap, specialPermissions } from '../permissions';
import { isAdmin } from '../utils';

// Check if user can access a resource (has read permission)
function canAccessResource(resource: string, permissions: string[]): boolean {
  // Admin can access everything
  if (isAdmin()) return true;

  // Check for special (non-standard) permission first
  if (specialPermissions[resource]) {
    return permissions.includes(specialPermissions[resource]);
  }

  const permissionResource = resourcePermissionMap[resource];
  if (!permissionResource) return true; // Unknown resource, allow by default

  // Check for read permission
  const readPermission = `${permissionResource}:read`;
  return permissions.includes(readPermission);
}

// Check if any child resource is accessible
function isAnyChildAccessible(
  childrenDefs: { resource: string }[],
  permissions: string[]
): boolean {
  // Admin can see everything
  if (isAdmin()) return true;

  return childrenDefs.some(({ resource }) =>
    canAccessResource(resource, permissions)
  );
}

export const CustomMenu = () => {
  const { permissions = [] } = usePermissions<string[]>();

  const hrChildren = React.useMemo(
    () => [
      { resource: 'job-application', icon: <AssignmentIcon /> },
      { resource: 'job-application-source', icon: <SourceIcon /> },
      { resource: 'job-application-status', icon: <LabelIcon /> },
      { resource: 'job-application-closure-reason', icon: <BlockIcon /> },
      // { resource: 'job-application-tag', icon: <LocalOfferIcon /> },
    ],
    []
  );

  const registrationsChildren = React.useMemo(
    () => [
      { resource: 'user', icon: <PeopleIcon /> },
      { resource: 'group', icon: <GroupIcon /> },
      { resource: 'courses-management', icon: <SchoolIcon /> },
      { resource: 'company', icon: <BusinessIcon /> },
      { resource: 'role', icon: <SecurityIcon /> },
    ],
    []
  );

  const biChildren = React.useMemo(
    () => [
      { resource: 'job-application-dashboard', icon: <InsightsIcon /> },
      {
        resource: 'business-intelligence/market-analysis',
        icon: <BarChartIcon />,
      },
      { resource: 'business-intelligence/sales', icon: <TrendingUpIcon /> },
      {
        resource: 'business-intelligence/geographic-analysis',
        icon: <InsightsIcon />,
      },
      {
        resource: 'business-intelligence/pipeline-analysis',
        icon: <InsightsIcon />,
      },
      {
        resource: 'business-intelligence/irt',
        icon: <InsightsIcon />,
      },
    ],
    []
  );

  const chatChildren = React.useMemo(
    () => [
      { resource: 'tickets', icon: <ConfirmationNumberIcon /> },
      { resource: 'conversations', icon: <ForumIcon /> },
      { resource: 'contact', icon: <ContactsIcon /> },
      { resource: 'channel', icon: <SettingsInputAntennaIcon /> },
      { resource: 'ticket-queue', icon: <QueueIcon /> },
      { resource: 'ticket-quick-reply', icon: <QuickreplyIcon /> },
      { resource: 'ticket-tag', icon: <LocalOfferIcon /> },
      { resource: 'conversation-tag', icon: <LocalOfferIcon /> },
    ],
    []
  );

  const hrVisible = isAnyChildAccessible(hrChildren, permissions);
  const regVisible = isAnyChildAccessible(registrationsChildren, permissions);
  const biVisible = isAnyChildAccessible(biChildren, permissions);
  // const chatVisible = isAnyChildAccessible(chatChildren, permissions);
  const chatVisible = false;

  return (
    <MultiLevelMenu initialOpen>
      {/* My Courses Section - Always visible for authenticated users */}
      <MultiLevelMenu.ResourceItem resource="my-courses" icon={<SchoolIcon />} />

      {/* Chat/Support Section */}
      {chatVisible && (
        <MultiLevelMenu.Item
          name="chat"
          icon={<SupportAgentIcon />}
          label="menu.chat"
        >
          {chatChildren.map(({ resource, icon }) => (
            <MultiLevelMenu.ResourceItem
              key={resource}
              resource={resource}
              icon={icon}
            />
          ))}
        </MultiLevelMenu.Item>
      )}

      {/* HR Section */}
      {hrVisible && (
        <MultiLevelMenu.Item name="hr" icon={<WorkIcon />} label="menu.hr">
          {hrChildren.map(({ resource, icon }) => (
            <MultiLevelMenu.ResourceItem
              key={resource}
              resource={resource}
              icon={icon}
            />
          ))}
        </MultiLevelMenu.Item>
      )}

      {/* Registrations Section */}
      {regVisible && (
        <MultiLevelMenu.Item
          name="registrations"
          icon={<PeopleIcon />}
          label="menu.registrations"
        >
          {registrationsChildren.map(({ resource, icon }) => (
            <MultiLevelMenu.ResourceItem
              key={resource}
              resource={resource}
              icon={icon}
            />
          ))}
        </MultiLevelMenu.Item>
      )}

      {/* Business Intelligence Section */}
      {biVisible && (
        <MultiLevelMenu.Item
          name="bi"
          icon={<AssessmentIcon />}
          label="menu.bi"
        >
          {biChildren.map(({ resource, icon }) => (
            <MultiLevelMenu.ResourceItem
              key={resource}
              resource={resource}
              icon={icon}
            />
          ))}
        </MultiLevelMenu.Item>
      )}
    </MultiLevelMenu>
  );
};
